import React, { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Non-blocking amber banner shown on the dashboard when a trial user has
 * 3 days or fewer remaining. Invisible for beta / active / samcart / expired
 * (expired users see the blocking TrialExpiredGate instead).
 */
export default function TrialEndingBanner() {
  const { practice } = useAuth();

  const daysRemaining = useMemo(() => {
    const status = practice?.subscription?.status;
    const source = practice?.source;
    if (source === 'samcart') return null;
    if (status !== 'trial') return null;

    const endsAt = practice?.subscription?.trialEndsAt;
    if (!endsAt) return null;

    try {
      const ms = new Date(endsAt).getTime() - Date.now();
      if (Number.isNaN(ms)) return null;
      return Math.ceil(ms / (1000 * 60 * 60 * 24));
    } catch {
      return null;
    }
  }, [practice]);

  // Show only on the final 3 days; expired handled by the blocking gate
  if (daysRemaining === null || daysRemaining > 3 || daysRemaining < 1) return null;

  const handleSubscribe = () => {
    window.location.assign('/plans-billing');
  };

  const label =
    daysRemaining === 1
      ? 'Your free trial ends tomorrow.'
      : `Your free trial ends in ${daysRemaining} days.`;

  return (
    <div
      data-testid="trial-ending-banner"
      style={{
        width: '100%',
        background: 'linear-gradient(90deg, #fef3c7 0%, #fde68a 100%)',
        borderBottom: '2px solid #f59e0b',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        flexWrap: 'wrap',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        fontSize: '14px',
        color: '#78350f',
        fontWeight: 600,
        position: 'sticky',
        top: 0,
        zIndex: 50,
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      }}
    >
      <span style={{ fontSize: '18px' }} aria-hidden="true">⏰</span>
      <span>
        <strong>{label}</strong>{' '}
        <span style={{ fontWeight: 500 }}>
          Subscribe now to keep full access — no interruption.
        </span>
      </span>
      <button
        data-testid="trial-banner-subscribe-btn"
        onClick={handleSubscribe}
        style={{
          background: '#b45309',
          color: '#fff',
          border: 'none',
          padding: '8px 18px',
          borderRadius: '8px',
          fontWeight: 700,
          fontSize: '13px',
          cursor: 'pointer',
          boxShadow: '0 2px 6px rgba(180, 83, 9, 0.3)',
          whiteSpace: 'nowrap',
          transition: 'background 0.15s',
        }}
        onMouseOver={(e) => (e.currentTarget.style.background = '#92400e')}
        onMouseOut={(e) => (e.currentTarget.style.background = '#b45309')}
      >
        Subscribe Now →
      </button>
    </div>
  );
}
